import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { GripVertical, Plus, Pencil, Trash2, Check, X } from 'lucide-react'
import { motion, AnimatePresence, Reorder } from 'framer-motion'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from '@/components/ui/toaster'

interface PipelineStage {
  id: string
  name: string
  color: string
  order: number
  isDefault?: boolean
}

interface PipelineStageManagerProps {
  jobId: string
  stages: PipelineStage[]
  onUpdate?: () => void
}

const stageColors = [
  { value: '#6b7280', label: 'Gray' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#10b981', label: 'Emerald' },
  { value: '#ef4444', label: 'Red' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#06b6d4', label: 'Cyan' },
]

export function PipelineStageManager({
  jobId,
  stages: initialStages,
  onUpdate,
}: PipelineStageManagerProps) {
  const [stages, setStages] = useState<PipelineStage[]>(initialStages)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [editColor, setEditColor] = useState('')
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [newStageName, setNewStageName] = useState('')
  const [newStageColor, setNewStageColor] = useState('#6b7280')

  const queryClient = useQueryClient()

  const updateStages = useMutation({
    mutationFn: (updatedStages: Partial<PipelineStage>[]) =>
      api.put(`/jobs/${jobId}/stages`, { stages: updatedStages }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
      onUpdate?.()
      toast({ title: 'Pipeline stages updated', variant: 'success' })
    },
    onError: () => {
      toast({ title: 'Failed to update stages', variant: 'destructive' })
    },
  })

  const addStage = useMutation({
    mutationFn: (data: { name: string; color: string }) =>
      api.post(`/jobs/${jobId}/stages`, data),
    onSuccess: (newStage: any) => {
      setStages([...stages, newStage])
      setIsAddingNew(false)
      setNewStageName('')
      setNewStageColor('#6b7280')
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
      toast({ title: 'Stage added', variant: 'success' })
    },
    onError: () => {
      toast({ title: 'Failed to add stage', variant: 'destructive' })
    },
  })

  const deleteStage = useMutation({
    mutationFn: (stageId: string) =>
      api.delete(`/jobs/${jobId}/stages/${stageId}`),
    onSuccess: (_, stageId) => {
      setStages(stages.filter((s) => s.id !== stageId))
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
      toast({ title: 'Stage deleted', variant: 'success' })
    },
    onError: () => {
      toast({ title: 'Failed to delete stage', variant: 'destructive' })
    },
  })

  const handleReorder = (newOrder: PipelineStage[]) => {
    const reordered = newOrder.map((stage, index) => ({
      ...stage,
      order: index,
    }))
    setStages(reordered)
    updateStages.mutate(
      reordered.map((s) => ({ id: s.id, order: s.order }))
    )
  }

  const startEditing = (stage: PipelineStage) => {
    setEditingId(stage.id)
    setEditValue(stage.name)
    setEditColor(stage.color)
  }

  const saveEdit = () => {
    if (!editingId || !editValue.trim()) return

    const updatedStages = stages.map((s) =>
      s.id === editingId ? { ...s, name: editValue.trim(), color: editColor } : s
    )
    setStages(updatedStages)
    updateStages.mutate([{ id: editingId, name: editValue.trim(), color: editColor }])
    setEditingId(null)
    setEditValue('')
    setEditColor('')
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditValue('')
    setEditColor('')
  }

  const handleAddStage = () => {
    if (!newStageName.trim()) return
    addStage.mutate({ name: newStageName.trim(), color: newStageColor })
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Pipeline Stages</CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsAddingNew(true)}
          disabled={isAddingNew}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Stage
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Drag to reorder stages. Candidates will move through these stages during the hiring process.
        </p>

        <Reorder.Group
          axis="y"
          values={stages}
          onReorder={handleReorder}
          className="space-y-2"
        >
          <AnimatePresence mode="popLayout">
            {stages.map((stage) => (
              <Reorder.Item
                key={stage.id}
                value={stage}
                className="list-none"
              >
                <motion.div
                  layout
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border group hover:border-border/80 transition-colors"
                >
                  <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab active:cursor-grabbing" />

                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: stage.color }}
                  />

                  {editingId === stage.id ? (
                    <div className="flex-1 flex items-center gap-2">
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="h-8"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit()
                          if (e.key === 'Escape') cancelEdit()
                        }}
                      />
                      <div className="flex gap-1">
                        {stageColors.map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${
                              editColor === color.value
                                ? 'border-foreground'
                                : 'border-transparent'
                            }`}
                            style={{ backgroundColor: color.value }}
                            onClick={() => setEditColor(color.value)}
                            title={color.label}
                          />
                        ))}
                      </div>
                      <Button size="icon" variant="ghost" onClick={saveEdit}>
                        <Check className="h-4 w-4 text-success" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={cancelEdit}>
                        <X className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <span className="flex-1 font-medium">{stage.name}</span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => startEditing(stage)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {!stage.isDefault && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => deleteStage.mutate(stage.id)}
                            disabled={deleteStage.isPending}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </motion.div>
              </Reorder.Item>
            ))}
          </AnimatePresence>
        </Reorder.Group>

        {/* Add new stage form */}
        <AnimatePresence>
          {isAddingNew && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 p-4 border border-dashed border-border rounded-lg"
            >
              <div className="space-y-3">
                <Input
                  placeholder="Stage name"
                  value={newStageName}
                  onChange={(e) => setNewStageName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddStage()
                    if (e.key === 'Escape') {
                      setIsAddingNew(false)
                      setNewStageName('')
                    }
                  }}
                />
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Color:</span>
                  {stageColors.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
                        newStageColor === color.value
                          ? 'border-foreground'
                          : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color.value }}
                      onClick={() => setNewStageColor(color.value)}
                      title={color.label}
                    />
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsAddingNew(false)
                      setNewStageName('')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAddStage}
                    loading={addStage.isPending}
                    disabled={!newStageName.trim()}
                  >
                    Add Stage
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  )
}
